import React from "react";

function clean(value) {
  return String(value ?? "").trim();
}

function setupProgress(projectPlan) {
  const overview = projectPlan?.overview || {};

  const checks = [
    overview.subjectOrDiscipline,
    overview.intendedLearners,
    overview.educationalLevel,
    overview.editionOrVersion,
  ];

  const completed = checks.filter((value) => clean(value)).length;

  return {
    completed,
    total: checks.length,
    ready: completed === checks.length,
  };
}

export default function TextbookBlueprintOverview({
  projectPlan,
  onOpenProjectPlan,
  onOpenBookStructure,
  onOpenWrite,
  bookMapEnabled = false,
  chapterWorkspaceEnabled = false,
  isFR = false,
}) {
  const items = Array.isArray(projectPlan?.bookMap?.items)
    ? projectPlan.bookMap.items
    : [];

  const chapters = items.filter(
    (item) => clean(item?.type) === "chapter"
  ).length;

  const sections = items.filter(
    (item) => clean(item?.type) === "section"
  ).length;

  const progress = setupProgress(projectPlan);
  const structureReady = chapters > 0;

  /*
   * The main Textbook Studio navigator already exposes Setup,
   * Book Structure and Write. This surface therefore does one job:
   * choose the most useful next action for the writer.
   */
  const nextStep = !progress.ready
    ? {
        eyebrow: isFR ? "PROCHAINE ÉTAPE" : "NEXT STEP",
        title: isFR ? "Compléter les détails du livre" : "Complete Book Details",
        text: isFR
          ? "Renseignez les informations essentielles du manuel. NextScenes vous guidera ensuite vers la structure."
          : "Finish the essential textbook details. NextScenes will then guide you to Book Structure.",
        action: isFR ? "Continuer les détails" : "Continue Book Details",
        onOpen: onOpenProjectPlan,
        enabled: typeof onOpenProjectPlan === "function",
      }
    : !structureReady
      ? {
          eyebrow: isFR ? "PROCHAINE ÉTAPE" : "NEXT STEP",
          title: isFR ? "Créer la structure du livre" : "Build the Book Structure",
          text: isFR
            ? "Ajoutez au moins un chapitre. NextScenes ouvrira ensuite la rédaction."
            : "Add at least one Chapter. NextScenes will then make writing available.",
          action: isFR ? "Ouvrir la structure" : "Open Book Structure",
          onOpen: onOpenBookStructure,
          enabled:
            bookMapEnabled &&
            typeof onOpenBookStructure === "function",
        }
      : {
          eyebrow: isFR ? "CONTINUER" : "CONTINUE",
          title: isFR ? "Poursuivre la rédaction" : "Continue Writing",
          text: isFR
            ? `${chapters} chapitre(s) sont prêts. Continuez le manuscrit dans Écrire.`
            : `${chapters} Chapter(s) are ready. Continue the manuscript in Write.`,
          action: isFR ? "Aller à Écrire" : "Go to Write",
          onOpen: onOpenWrite,
          enabled:
            chapterWorkspaceEnabled &&
            typeof onOpenWrite === "function",
        };

  return (
    <section
      data-textbook-studio-home="true"
      className="ns-textbook-studio-home"
      style={{
        display: "grid",
        gap: 12,
      }}
    >
      <section
        className="ns-card ns-textbook-studio-next-step"
        data-textbook-next-step="true"
        style={{
          padding: 18,
          borderRadius: 18,
          border:
            "1px solid rgba(217,119,6,0.22)",
          background:
            "linear-gradient(135deg, rgba(255,251,235,0.96), rgba(255,255,255,0.98))",
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            maxWidth: 760,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing:
                "0.12em",
              textTransform:
                "uppercase",
              color: "#b45309",
            }}
          >
            {nextStep.eyebrow}
          </div>

          <h3
            className="ns-card-title"
            style={{
              margin: "5px 0",
              fontSize: 22,
            }}
          >
            {nextStep.title}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.55,
            }}
          >
            {nextStep.text}
          </p>
        </div>

        <button
          className="ns-button ns-button-primary"
          type="button"
          disabled={!nextStep.enabled}
          onClick={nextStep.onOpen}
        >
          {nextStep.action}
        </button>
      </section>

      <div
        data-textbook-studio-summary="true"
        className="ns-card-meta"
        role="status"
        style={{
          padding: "2px 4px",
          lineHeight: 1.5,
          fontWeight: 750,
          color: "#475569",
        }}
      >
        <strong
          style={{
            color: "#334155",
          }}
        >
          {isFR
            ? `Préparation ${progress.completed}/${progress.total}`
            : `Setup ${progress.completed}/${progress.total}`}
        </strong>
        {" · "}
        {isFR
          ? `${chapters} chapitre(s)`
          : `${chapters} Chapter(s)`}
        {" · "}
        {isFR
          ? `${sections} section(s)`
          : `${sections} Section(s)`}
      </div>
    </section>
  );
}
